import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import BanksList from "../../features/manageBanks/banksList";
import BanksTopbar from "../../features/manageBanks/topbar";
import { useEffect, useState } from "react";
import {
  useCreateBankMutation,
  useUpdateBankMutation,
  useLazyDeleteBankQuery,
} from "../../services/banks";
import { showError, showToast } from "../../constants/toasts";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import BankDetails from "../../Modals/bankDetails";
import usePermission from "../../hooks/usePermission";
import useTranslation from "../../hooks/traslation";
import useBanks from "../../hooks/useBanks";
import type { PageChangeHandler, BankTableRow } from "../../types/api";
import { getErrorMessage } from "../../types/api";
import type { BankListItem } from "../../reducers/banksSlice";

const ManageBanks = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [createBank] = useCreateBankMutation();
  const [updateBankMutation] = useUpdateBankMutation();
  const [deleteBank] = useLazyDeleteBankQuery();
  const [selectedId, setSelectedId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);

  const {
    banks: reduxBanksList,
    isLoading: isBanksLoading,
    error: banksError,
    addBankToStore,
    updateBankInStore,
    removeBankFromStore,
  } = useBanks({ fetchOnMount: true });

  const [name, setName] = useState<string>("");


  const [allRows, setAllRows] = useState<BankTableRow[]>([]);
  const [filteredRows, setFilteredRows] = useState<BankTableRow[]>([]);
  const [rows, setRows] = useState<BankTableRow[]>([]);

  const [page, setPage] = useState<number>(1);
  const itemsPerPage = 10;
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");

  const translate = useTranslation();

  const handlePageChange: PageChangeHandler = (event, newPage) => {
    setPage(newPage);
  };

  useEffect(() => {
    if (reduxBanksList.length > 0) {
      setAllRows(reduxBanksList);
    }
  }, [reduxBanksList]);

  useEffect(() => {
    if (banksError) {
      showError(
        translate?.banks?.failedToLoadBanks || banksError
      );
    }
  }, [banksError]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    let filtered = allRows;
    if (debouncedSearchTerm.trim()) {
      filtered = allRows.filter((row: BankTableRow) =>
        row?.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      );
    }
    setFilteredRows(filtered);

    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedRows = filtered.slice(startIndex, endIndex);
    setRows(paginatedRows);
  }, [allRows, debouncedSearchTerm, page]);

  const handleSubmit = async () => {
    setIsFormLoading(true);
    const body = {
      name,
    };

    if (selectedId) {
      try {
        const response = await updateBankMutation({
          body,
          id: selectedId,
        }).unwrap();
        if (response?.statusCode === 200) {
          const updated = response?.data ?? body;
          const updatedRow = {
            ...updated,
            _id: selectedId,
            id: selectedId,
          };
          setAllRows((prev) =>
            prev.map((r) =>
              r._id === selectedId ? { ...r, ...updatedRow } : r
            )
          );
          updateBankInStore(selectedId, updatedRow);
          showToast(
            translate?.banks?.bankUpdatedSuccess ||
              "Bank updated successfully"
          );
          setOpen(false);
          resetForm();
        }
      } catch {
        showError(
          translate?.banks?.failToUpdate || "Failed to update bank"
        );
      } finally {
        setIsFormLoading(false);
      }
    } else {
      try {
        const response = await createBank({ data: body }).unwrap();
        if (response?.statusCode === 200) {
          const created = response?.data;
          if (created) {
            const newRow: BankListItem = {
              ...created,
              id: created._id,
              _id: created._id,
            };
            setAllRows((prev) => [...prev, newRow]);
            addBankToStore(newRow);
          }
          showToast(
            translate?.banks?.bankCreatedSuccess ||
              "Bank created successfully"
          );
          setOpen(false);
          resetForm();
        }
      } catch (error: unknown) {
        showError(getErrorMessage(error, "Failed to create bank"));
      } finally {
        setIsFormLoading(false);
      }
    }
  };

  const resetForm = () => {
    setName("");
    setSelectedId("");
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response = await deleteBank({ id: selectedId }).unwrap();
      setIsLoading(false);

      if (response?.statusCode === 200) {
        showToast(
          translate?.banks?.bankDeletedSuccess ||
            "Bank deleted successfully"
        );
        removeBankFromStore(selectedId);
        const remainingCount = filteredRows.length - 1;
        if (
          remainingCount > 0 &&
          rows.length === 1 &&
          page > 1
        ) {
          const maxPage = Math.ceil(remainingCount / itemsPerPage);
          setPage(Math.min(page - 1, maxPage));
        }
        setAllRows((prev) => prev.filter((r) => r._id !== selectedId));
        setOpenDelete(false);
        setSelectedId("");
      }
    } catch {
      showError("Failed to delete bank");
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpen(false);
    resetForm();
  };

  const handleCloseDetailsModal = () => {
    setOpenDetails(false);
    setSelectedId("");
  };

  const handleEditClick = (row: {
    _id: string;
    name?: string;
  }) => {
    setSelectedId(row._id);
    setName(row.name || "");
    setOpen(true);
  };

  return (
    <MainContainer isLoading={isBanksLoading}>
      <div className="main_title">
        <h1>Manage Banks</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>
            {translate?.dashboard?.dashboard || "Dashboard"}
          </span>{" "}
          Manage Banks
        </p>
      </div>

      <div className="cards">
        <BanksTopbar
          name={name}
          setName={setName}
          handleSubmit={handleSubmit}
          setOpen={setOpen}
          open={open}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          setSearchTerm={setSearchTerm}
          setDebouncedSearchTerm={setDebouncedSearchTerm}
          searchTerm={searchTerm}
          isFormLoading={isFormLoading}
        />
        <BanksList
          setSelectedId={setSelectedId}
          setOpen={setOpen}
          open={open}
          name={name}
          setName={setName}
          openDelete={openDelete}
          setOpenDelete={setOpenDelete}
          rows={rows}
          setRows={setRows}
          openDetails={openDetails}
          setOpenDetails={setOpenDetails}
          isLoading={isLoading}
          handleEditClick={handleEditClick}
          handlePageChange={handlePageChange}
          setPage={setPage}
          page={page}
          totalCount={filteredRows.length}
        />
      </div>
      <ConfirmationModal
        text={
          translate?.banks?.confirmDeleteBank ||
          "Are you sure you want to delete this bank?"
        }
        openModal={openDelete}
        setOpenModal={setOpenDelete}
        handleSubmit={handleDelete}
        isLoading={isLoading}
      />
      <BankDetails
        open={openDetails}
        name={name}
        onClose={handleCloseDetailsModal}
        selectedId={selectedId}
      />
    </MainContainer>
  );
};

export default ManageBanks;
